import Button from "./../../Components/Button";
import { InputGroup } from "@components/_Revamp/InputGroup";
import { Link as LinkDom, useHistory } from "react-router-dom";
import { Text } from "./../../Components/Text";
import { styled } from "@linaria/react";
import { ROUTE_PATH } from "@static/route-paths";
import React, { useEffect, useRef, useState } from "react";
import { ITheme, useTheme } from "src/theme-revamp";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import {
  updateBMI,
  updateBmiCheckNumber,
} from "@redux/slices/SHIELD_STARTER/step4Slice";
import { get, isNumber, lowerCase } from "lodash";
import { postBMICheck, postBMICheckShield } from "@api/index";
import { BMICheckRequest } from "@models/BMIRequest.model";
import { SCREEN_SIZE } from "@static/screen-size";
import ErrorModal from "@pages/ShieldStarter/Components/Modals/ErrorModal";

const BmiValue = styled.p<{ theme?: ITheme; color?: string }>`
  color: ${(props) => props.theme?.colors.grey[1400]};
  font-weight: 600;
  font-size: 12px;
  line-height: 1.4;
  color: ${(props) => (props.color ? props.color : "#8a8988")};
  letter-spacing: 0.04em;
  span {
    color: ${(props) => props.theme?.colors.primary!};
    font-weight: 600;
  }
`;

const BMIContainer = styled.div`
  display: flex;
  align-items: center;
  // justify-content: center;
  column-gap: 12px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 16px;
  margin: 0 0 64px 0;
`;

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  margin-bottom: 36px;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    margin-bottom: 80px;
  }
`;

const HeightAndWeightContainer = styled.div`
  max-width: 588px;
  margin: 0 auto 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (max-width: 768px) {
    margin: 0 auto 0 auto;
  }
`;

export interface HeightAndWeightModel {
  height: number;
  weight: number;
}
export interface HeightAndWeightProps {
  values?: HeightAndWeightModel;
  onComplete: (heightAndWeight: HeightAndWeightModel) => void;
  screenSize: number;
  resetUW: () => void;
}

export const HeightAndWeight = ({
  values,
  onComplete,
  screenSize,
  resetUW,
}: HeightAndWeightProps) => {
  const dispatch = useAvivaDispatch();
  const history = useHistory();
  const whomToBuy = useAvivaSelector(
    (state) => state.shieldStarterStep1.whomToBuy
  );
  const personalInfo: any = useAvivaSelector(
    (state) => state.shieldStarterStep3.personalInfo
  );
  const selectedChildInfo: any = useAvivaSelector(
    (state) => state.shieldStarterStep3.selectedChildInfo
  );
  const theme = useTheme();
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [weight, setWeight] = useState<number | undefined>(undefined);
  const heightInputRef = useRef<HTMLInputElement>(null);
  const weightInputRef = useRef<HTMLInputElement>(null);
  const [BMI, setBMI] = useState<number | null>(null);
  const [isValidInput, setIsValidInput] = useState(false);
  const [isValidBMI, setIsValidBMI] = useState(false);
  const [isButtonDisabled, setButtonDisabled] = useState(true);
  const [onLoadErrorHeight, setOnLoadErrorHeight] = useState<string | null>(
    null
  );
  const [onLoadErrorWeight, setOnLoadErrorWeight] = useState<string | null>(
    null
  );
  const [isValueChanged, setIsValueChanged] = useState(true);
  const btnGotIt_Clicked = () => {
    if (height && weight) onComplete({ height, weight });
  };
  const bmiCheckedAttempts = useAvivaSelector(
    (state) => state.shieldStarterStep4.bmiChecked
  );

  const HeightInput_Changed = (e: any) => {
    const value = e.target.value.replace(".", "");
    resetUW();
    setIsValueChanged(true);
    setBMI(null);
    setIsValidInput(false);
    setIsValidBMI(false);
    setHeight(value);
    value && (value < 40 || value > 250)
      ? setOnLoadErrorHeight("Please enter a value between 40 - 250")
      : !(value && value > 0)
      ? setOnLoadErrorHeight("Please enter valid height!")
      : setOnLoadErrorHeight(null);
    if (e.target.value.length >= 3 && weightInputRef.current) {
      weightInputRef.current?.focus();
    }
  };

  const WeightInput_Changed = (e: any) => {
    const value = e.target.value.replace(".", "");
    resetUW();
    setIsValueChanged(true);
    setBMI(null);
    setIsValidInput(false);
    setIsValidBMI(false);
    setWeight(value);
    value && (value < 1 || value > 300)
      ? setOnLoadErrorWeight("Please enter a value between  1-300")
      : !(value && value > 0)
      ? setOnLoadErrorWeight("Please enter valid weight!")
      : setOnLoadErrorWeight(null);
    if (e.target.value.length >= 3 && weightInputRef.current) {
      weightInputRef.current?.blur();
    }
  };

  const checkBmiIsValid = async (values: any) => {
    const { weight, height } = values;
    const age =
      whomToBuy === "child" ? selectedChildInfo?.age : personalInfo?.age;
    try {
      const payload = {
        weight,
        height,
        age,
        comp: "dfgh",
        prodcode: "SHIELD_STARTER",
        customerId: personalInfo?.customerId
      };
      const response = await postBMICheckShield(payload);
      if (!values.noIncreaseAttemptCount) {
        dispatch(updateBmiCheckNumber(bmiCheckedAttempts + 1));
      }
      const status = get(response, "data.status", "");
      const bmi = Number(get(response, "data.bmi", ""));
      console.debug(`QnB ➤ checkBmi() ➤ `, {
        request: payload,
        response,
        status,
        bmi,
        result: lowerCase(status) !== "decline" && bmi >= 17 && bmi <= 29,
      });
      return { bmi, status };
    } catch (error) {
      console.error(error);
      history.push(ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR);
      return {};
    }
  };

  const CheckBMI = async (
    height: number,
    weight: number,
    noIncreaseAttemptCount: boolean = false
  ) => {
    if (bmiCheckedAttempts > 3) {
      return;
    }

    if (height && weight) {
      // const bmiValue = Math.floor(weight / Math.pow(height / 100, 2));
      try {
        const { bmi, status } = await checkBmiIsValid({
          height,
          weight,
          noIncreaseAttemptCount,
        });
        setIsValidInput(true);
        setBMI(Number(bmi) || 0);
        if (lowerCase(status) === "accept") {
          dispatch(updateBMI(bmi || 0));
          setIsValidBMI(true);
        } else {
          setIsValueChanged(false);
          setIsValidBMI(false);
        }
      } catch (e) {
        history.push(ROUTE_PATH.SHIELD_STARTER_GENERIC_ERROR);
      }
    }
  };

  useEffect(() => {
    if (values && values.height && values.weight) {
      setHeight(values.height);
      setWeight(values.weight);
      CheckBMI(values.height, values.weight, true);
    }
  }, []);
  
  useEffect(() => {
    if (isValidInput && !isValidBMI && !isValueChanged) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(
        isValidInput
          ? !isValidBMI
          : !(
              height &&
              height >= 40 &&
              height <= 300 &&
              weight &&
              weight >= 1 &&
              weight <= 300
            )
      );
    }
  }, [isValidInput, isValidBMI, isValueChanged, height, weight]);
  let TITLE_TEXT =
    "Let's find out more about your health, what's your current height and weight?";
  let DISCRIPTION_TEXT =
    "Enter your details and hit 'Check' for a quick fact about your body!";

  if (whomToBuy === "child") {
    TITLE_TEXT =
      "Let's find out more about your child's health, what's his/her current height and weight?";
    DISCRIPTION_TEXT =
      "Enter your child's details and hit 'Check' for a quick fact about his/her body";
  }

  const DISC_FONT_WEIGHT = screenSize >= SCREEN_SIZE.mobilesUp ? 600 : 400;

  return (
    <HeightAndWeightContainer>
      <Header>
        <Text variant="title" align="center" maxWidht={588}>
          {TITLE_TEXT}
        </Text>
        <Text
          variant="base"
          fontWeight={DISC_FONT_WEIGHT}
          color="#8A8988"
          align="center"
        >
          {DISCRIPTION_TEXT}
        </Text>
      </Header>

      <InputContainer>
        <InputGroup
          ref={heightInputRef}
          id="height"
          label="Height (cm)"
          placeholder=""
          onChange={(e: any) => {
            if (e.target.value === "") {
              HeightInput_Changed(e);
              return;
            }
            if (
              isNaN(e.target.value) ||
              !isNumber(+e.target.value) ||
              e.target.value === "0"
            ) {
              return;
            }
            e.target.value = parseInt(e.target.value);
            const regex = /^\s*.{0,3}\s*$/;
            const value = e.target.value;
            if (regex.test(value)) HeightInput_Changed(e);
          }}
          invalid={!!onLoadErrorHeight}
          noBluRequired={true}
          errorMessage={onLoadErrorHeight || ""}
          pattern="\d*"
          inputProps={{
            type: "numeric",
            value: height,
            maxLength: 3,
          }}
          textAlign="left"
          inputType={"input"}
        />
        <InputGroup
          ref={weightInputRef}
          errorMessage={onLoadErrorWeight || ""}
          invalid={!!onLoadErrorWeight}
          noBluRequired={true}
          id="weight"
          label="Weight (kg)"
          placeholder=""
          pattern="\d*"
          onChange={(e: any) => {
            if (e.target.value === "") {
              WeightInput_Changed(e);
              return;
            }
            if (
              isNaN(e.target.value) ||
              !isNumber(+e.target.value) ||
              e.target.value === "0"
            ) {
              return;
            }
            e.target.value = parseInt(e.target.value);
            const regex = /^\s*.{0,3}\s*$/;
            const value = e.target.value;
            if (regex.test(value)) WeightInput_Changed(e);
          }}
          inputProps={{
            type: "numeric",
            value: weight,
            maxLength: 3,
          }}
          textAlign="left"
          inputType={"input"}
        />

        {isValidInput &&
          (isValidBMI ? (
            <BMIContainer>
              <BmiValue theme={theme}>
                Your Body Mass Index (BMI) is <span>{BMI}</span>
              </BmiValue>
            </BMIContainer>
          ) : (
            <BmiValue theme={theme}>
              Your Body Mass Index (BMI) is <span> {BMI || 0}</span> and this
              product may not be suitable for you
            </BmiValue>
          ))}
        {/* {(onLoadErrorHeight || onLoadErrorWeight) &&  
            <BmiValue theme={theme} color="#C50000">
              {onLoadErrorHeight || onLoadErrorWeight}
            </BmiValue>
          } */}
      </InputContainer>

      <Button
        label={
          isValidInput && isValidBMI
            ? "Continue"
            : isValueChanged
            ? "Check"
            : "Contact us"
        }
        width="180px"
        onClick={() => {
          if (isValidInput && !isValidBMI && !isValueChanged) {
            history.push(ROUTE_PATH.SHIELD_STARTER_CONTACT_US);
            return;
          }
          if (isValidBMI) {
            btnGotIt_Clicked();
            return;
          }

          if (height && height > 0 && weight && weight > 0) {
            CheckBMI(height, weight);
          }
        }}
        disabled={isButtonDisabled}
      />
      {bmiCheckedAttempts > 3 && (
        <ErrorModal
          title="You may need some assistance"
          error="It looks like you might need assistance from a Financial Adviser Representative
          to find a more suitable plan for your needs."
        />
      )}
    </HeightAndWeightContainer>
  );
};
